import type {INetsMap} from "@buildwithflux/models";
import {Records} from "@buildwithflux/shared";
import {createSelector} from "reselect";

import type {DocumentSelector, DocumentStoreState, ParameterizedSelector} from "../types";
import {emptyArray} from "../utils";

/**
 * Derived selectors for nets - computed values
 * These selectors perform filtering, mapping, or other computations on net data
 * Note: These use reselect for memoization of expensive computations
 */

/** Gets all net labels - memoized */
export const labels: DocumentSelector<string[]> = createSelector(
    [(state: DocumentStoreState) => state.nets],
    (nets): string[] => {
        if (!nets) return emptyArray;
        const result: string[] = [];
        for (const net of Records.iterate(nets)) {
            if (net.label) {
                result.push(net.label);
            }
        }
        return result;
    },
);

/** Gets nets filtered by a predicate - parameterized */
export const filtered: ParameterizedSelector<(net: INetsMap[string]) => boolean, Array<INetsMap[string]>> = (
    predicate: (net: INetsMap[string]) => boolean,
) => {
    return (state: DocumentStoreState): Array<INetsMap[string]> => {
        const nets = state.nets;
        if (!nets) return [];
        const result: Array<INetsMap[string]> = [];
        for (const net of Records.iterate(nets)) {
            if (predicate(net)) {
                result.push(net);
            }
        }
        return result;
    };
};

/** Checks if a net exists by UID */
export const exists: ParameterizedSelector<string, boolean> = (uid: string) => {
    return (state: DocumentStoreState): boolean => {
        return state.nets?.[uid] !== undefined;
    };
};

/** Gets net UIDs that match a predicate - parameterized */
export const uidsByPredicate: ParameterizedSelector<(net: INetsMap[string]) => boolean, string[]> = (
    predicate: (net: INetsMap[string]) => boolean,
) => {
    return (state: DocumentStoreState): string[] => {
        const nets = state.nets;
        if (!nets) return emptyArray;
        const result: string[] = [];
        for (const [uid, net] of Object.entries(nets)) {
            if (net && predicate(net)) {
                result.push(uid);
            }
        }
        return result;
    };
};

/** Gets the label of the first net from a list of UIDs */
export const firstLabelFromUids: ParameterizedSelector<string[], string> = (uids: string[]) => {
    return (state: DocumentStoreState): string => {
        const nets = state.nets;
        if (!nets) return "";
        for (const uid of uids) {
            const net = nets[uid];
            if (net) {
                return net.label;
            }
        }
        return "";
    };
};

/** Counts nets from a list of UIDs */
export const countFromUids: ParameterizedSelector<string[], number> = (uids: string[]) => {
    return (state: DocumentStoreState): number => {
        const nets = state.nets;
        if (!nets) return 0;
        let count = 0;
        for (const uid of uids) {
            if (nets[uid]) {
                count++;
            }
        }
        return count;
    };
};

/**
 * Count selectors - return primitive numbers to avoid useShallow overhead
 * These are optimized for components that only need to check counts or existence
 */

/** Gets count of net labels - memoized */
export const labelsCount: DocumentSelector<number> = createSelector(
    [(state: DocumentStoreState) => state.nets],
    (nets): number => {
        if (!nets) return 0;
        let count = 0;
        for (const net of Records.iterate(nets)) {
            if (net.label) {
                count++;
            }
        }
        return count;
    },
);
